const liveShoppingCostContent = `
  <p>For a beauty or personal-care brand, a four-hour TikTok Shop live usually needs about <strong>$1,000 in sales per hour</strong> before the host and crew are paid for.</p>

  <p>Whatnot’s average across its whole platform is about $308 per hour. A strong beauty brand with a working affiliate program reported about $2,070.</p>

  <p>That gap is huge. Live is not “cheap” or “expensive.” It has a high cost to run. After fees, ads, and returns, not much is left. Small changes in ad spend decide if you made money.</p>

  <p>The biggest cost is not the host. It is ads. Ads are billed on <em>all</em> sales, before returns. <strong>If the customer sends the product back, TikTok does not give that ad money back.</strong></p>

  <p>Here is the full math. Every number has a source. Swap in your own numbers.</p>

  <h2>First: ignore most “TikTok Shop fee” blogs</h2>

  <p>Start with TikTok’s cut. Three popular 2026 guides give three different fees.</p>

  <p>TikTok’s own seller docs, <a href="https://seller-us.tiktok.com/university/essay?knowledge_id=5988482086864682" target="_blank" rel="noopener noreferrer">dated 31 October 2024</a>, set the referral fee at <strong>6% for most categories</strong>. Most jewelry and pre-owned items are 5%. Collectibles and pre-owned sales pay 3% on the part of a sale above $10,000.</p>

  <p>Some blogs say the fee jumped to 8% in August 2026, or 9.5–11% for beauty and electronics. We could not find that. There is no Seller University page for a 2025 or 2026 fee hike. TikTok’s <a href="https://seller-us.tiktok.com/university/essay?knowledge_id=6747273381791534" target="_blank" rel="noopener noreferrer">July 2026 Policy Pulse</a> lists 16 seller updates. A fee change is not one of them. A jump that big would be news. It was not.</p>

  <p><strong>Use 6% until your own Seller Center shows something else.</strong> TikTok also does not publish a current public fee table. The official page is almost two years old. That is why blogs can invent numbers.</p>

  <p>Two real fee changes <em>are</em> on the books:</p>

  <ul>
    <li>Since 1 November 2025, TikTok charges <a href="https://seller-us.tiktok.com/university/essay?knowledge_id=1017269682849550" target="_blank" rel="noopener noreferrer">sales tax on the referral fee</a> for sellers in Texas and West Virginia. That is tax on TikTok’s cut, not on the product.</li>
    <li>As of July 2026, <strong>outbound shipping is not refunded</strong> when the buyer caused the return.</li>
  </ul>

  <h2>Two kinds of cost</h2>

  <p>This is the simple idea most cost pages skip.</p>

  <p>Some costs shrink when a customer returns. Some do not.</p>

  <p><strong>These come back, at least in part:</strong> creator commission (TikTok claws it back), most of the 6% referral fee (minus a refund fee, capped at $5 per SKU since May 2025), and product you can sell again.</p>

  <p><strong>These do not come back:</strong> ads, and return shipping. You paid to get the order. The order vanished. The ad money is gone. Return shipping is a new bill on top.</p>

  <p>US online returns are <strong>19.3%</strong>, per the <a href="https://nrf.com/media-center/press-releases/consumers-expected-to-return-nearly-850-billion-in-merchandise-in-2025" target="_blank" rel="noopener noreferrer">NRF and Happy Returns 2025 report</a>. That is about one order in five. If you treat every cost as if it hits the same sales number, you will look about 20% too optimistic.</p>

  <h2>A full four-hour session</h2>

  <p>This is a beauty brand. Average order is $50. Total sales are $10,000, or $2,500 an hour. That is a <em>good</em> session, not a typical one.</p>

  <p>GMV here just means sales. Gross means before returns. Net means after returns.</p>

  <div class="table-wrap">
    <table>
      <thead>
        <tr>
          <th>Line</th>
          <th>How we got it</th>
          <th>Amount</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Sales before returns</td>
          <td>200 orders × $50</td>
          <td>$10,000</td>
        </tr>
        <tr>
          <td>Returns</td>
          <td>19.3% (NRF online average)</td>
          <td>($1,930)</td>
        </tr>
        <tr>
          <td><strong>Sales after returns</strong></td>
          <td></td>
          <td><strong>$8,070</strong></td>
        </tr>
        <tr>
          <td>Cost of goods</td>
          <td>30% of sales after returns</td>
          <td>($2,421)</td>
        </tr>
        <tr>
          <td>Returned items you cannot resell</td>
          <td>15% of returned units, written off at cost</td>
          <td>($87)</td>
        </tr>
        <tr>
          <td>TikTok fee</td>
          <td>6% of sales after returns</td>
          <td>($484)</td>
        </tr>
        <tr>
          <td>Creator commission</td>
          <td>13% of sales after returns (taken back on returns)</td>
          <td>($1,049)</td>
        </tr>
        <tr>
          <td>Return shipping</td>
          <td>~39 returns × $8</td>
          <td>($312)</td>
        </tr>
        <tr>
          <td><strong>Ads (GMV Max)</strong></td>
          <td><strong>25% of sales before returns. Not refunded.</strong></td>
          <td><strong>($2,500)</strong></td>
        </tr>
        <tr>
          <td><strong>Left before paying the team</strong></td>
          <td></td>
          <td><strong>$1,217</strong></td>
        </tr>
        <tr>
          <td>Host</td>
          <td>4 hrs × $35</td>
          <td>($140)</td>
        </tr>
        <tr>
          <td>Chat moderator</td>
          <td>4 hrs × $28</td>
          <td>($112)</td>
        </tr>
        <tr>
          <td>Product helper</td>
          <td>4 hrs × $22</td>
          <td>($88)</td>
        </tr>
        <tr>
          <td>Editor / clipper</td>
          <td>2 hrs × $40</td>
          <td>($80)</td>
        </tr>
        <tr>
          <td>Prep</td>
          <td>2 hrs × $30</td>
          <td>($60)</td>
        </tr>
        <tr>
          <td>Gear (spread out)</td>
          <td>$4,000 setup ÷ 200 sessions</td>
          <td>($20)</td>
        </tr>
        <tr>
          <td><strong>What you keep</strong></td>
          <td></td>
          <td><strong>$717</strong></td>
        </tr>
      </tbody>
    </table>
  </div>

  <p><strong>On $10,000 of sales, this “good” session keeps about $717.</strong> That is 7.2 cents on the dollar. And it assumes a better hourly sales rate than the best public blended number for a real brand.</p>

  <p>One easy mistake: write off unsellable returns at the <em>retail</em> price. Do not. Write them off at <strong>cost</strong>. Cost of goods above already skipped those units. Using retail would overstate the loss by about $200 here and raise break-even by about 17%.</p>

  <h3>Why ads hurt the most</h3>

  <p>Since July 2025, <a href="https://ads.tiktok.com/resources/help/article/gmv-max-migration-tiktok-shop-ads?lang=en" target="_blank" rel="noopener noreferrer">GMV Max is the only TikTok Shop ad type</a>. You cannot switch to old-style bidding. You pick an ROI target. TikTok spends inside that target.</p>

  <p>ROI here means sales divided by ad spend. TikTok’s <a href="https://ads.tiktok.com/help/article/about-roi-protection-for-gmv-max-campaigns?lang=en" target="_blank" rel="noopener noreferrer">ROI protection formula</a> uses that idea, even if the help page never says it in plain English.</p>

  <blockquote>
    <p><strong>Ad spend as a share of sales = 1 ÷ your ROI target.</strong></p>
  </blockquote>

  <p>Target of 4: 25% of sales goes to ads. Target of 5: 20%. Target of 3: 33%.</p>

  <p>Treat 1 ÷ target as the best case. TikTok only pays you back if results fall below <strong>90%</strong> of your target. At a target of 4, ads can run to <strong>27.8% of sales</strong> and you still get nothing back.</p>

  <p>Ads are also billed on sales <em>before</em> returns. When one in five orders comes back, you still paid to get those orders.</p>

  <p>There is a second trap. While a GMV Max campaign is on, TikTok’s <a href="https://ads.tiktok.com/help/article/about-attribution-for-gmv-max?lang=en" target="_blank" rel="noopener noreferrer">attribution rules</a> credit the campaign with paid <em>and organic</em> orders. That includes people who never saw or clicked an ad. The ROI TikTok shows you looks better than the extra sales the ads actually caused. Only you can measure the gap, by comparing weeks with live and weeks without it.</p>

  <p>One ops note: change a LIVE campaign’s ROI target more than twice, and protection is off for that day. Tweaking mid-stream is not free.</p>

  <h3>What the team actually costs</h3>

  <p>Use job posts, not salary sites. ZipRecruiter’s average for “Live Shopping Host” in Los Angeles is $15.27 an hour. Not one real listing on that page is under $20. The average is pulled down by retail and hospitality jobs that only match the keywords.</p>

  <p>We looked at about 20 host posts in September 2026. Pay was <strong>$20 to $50 an hour, most often $25 to $40.</strong> SuperOrdinaryCo in West Hollywood listed $35–50. Popilush in New York listed $25–50. A Whatnot jewelry seller in Irvine listed $25–50. Lyve Studios in Los Angeles started at $30. Big-city talent can cost more.</p>

  <p>BeautyMatter, <a href="https://beautymatter.com/articles/tiktok-live-and-the-rise-of-a-new-role-in-beauty" target="_blank" rel="noopener noreferrer">quoting a former TikTok Live account manager in March 2026</a>, puts new hosts at $30–40 an hour. In New York or Los Angeles that can rise to $60–200. Top hosts can get $5,000–10,000 flat per session plus 5–10% commission.</p>

  <p>Moderators list at $21–35 an hour, on a smaller sample. Product help is real work. One listing told the host to “prepare and organize products before and after streams.”</p>

  <p><strong>Budget an editor.</strong> TikTok’s <a href="https://ads.tiktok.com/help/article/best-practices-for-live-gmv-max" target="_blank" rel="noopener noreferrer">LIVE GMV Max tips</a> ask for “at least 50–70 videos” to test, or 5–10 new videos a day. The auto-clip tool <a href="https://seller-us.tiktok.com/university/essay?knowledge_id=6769650398660398&amp;lang=en" target="_blank" rel="noopener noreferrer">makes at most five clips per session</a>. Each clip is under 60 seconds and shows one product. If you need more than that, a person is cutting video.</p>

  <p>Gear is the smallest line, even though most “cost of live” posts lead with it. A phone, ring light, tripod, and clip-on mic is a few hundred dollars. A nicer kit is $1,000–$3,000. A broadcast kit is $3,000 and up. Spread a $4,000 kit over 200 sessions and it is $20 a session. That is less than 30 minutes of your moderator.</p>

  <p>TikTok LIVE Studio and OBS are free. Third-party live tools often run $79–$259 a month. That matters if you run many brands. For one brand, it is a rounding error.</p>

  <h2>The number you can actually use: sales per hour</h2>

  <p>After ads, fees, product, and returns, this model keeps 12.17% of sales (before returns). The team and gear cost $500 for the session.</p>

  <p>$500 ÷ 0.1217 = <strong>$4,110 in sales for a four-hour live, or about $1,030 per hour, to break even.</strong></p>

  <p>Compare that to three public numbers:</p>

  <div class="table-wrap">
    <table>
      <thead>
        <tr>
          <th>Who</th>
          <th>Sales per hour</th>
          <th>Where it came from (and the catch)</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Whatnot, whole platform</td>
          <td>~$308</td>
          <td>Our math: $8B 2025 sales ÷ ~26M stream-hours. <strong>This is too low</strong> for the real 2025 average. See below.</td>
        </tr>
        <tr>
          <td>GoPure, all of 2025</td>
          <td>~$2,070</td>
          <td>$1M from 483 hours. <strong>Told to Glossy by GoPure’s marketing lead.</strong> Not audited. “Live-attributed revenue” is not defined.</td>
        </tr>
        <tr>
          <td>GoPure, launch day</td>
          <td>~$12,500</td>
          <td>$100,000+ in one 8-hour live. Same source. Same catch.</td>
        </tr>
      </tbody>
    </table>
  </div>

  <p>The Whatnot average is about a third of break-even. A strong brand with affiliates clears it. A launch day clears it by more than 10×.</p>

  <p>Do not treat $308 as a precise average. The $8 billion is all of 2025. The “500,000 hours a week” number is from Whatnot’s January 2026 report (via Tubefilter). Whatnot’s sales roughly doubled that year, so hours likely grew too. Year-end hours against full-year sales <strong>makes $308 too small</strong>. Treat it as a floor.</p>

  <p>So: is live shopping profitable? <em>It depends on your sales per hour.</em> Most teams have never run that number.</p>

  <h2>The one lever that changes everything</h2>

  <p>Keep every other number the same. Only change the GMV Max ROI target.</p>

  <div class="table-wrap">
    <table>
      <thead>
        <tr>
          <th>ROI target</th>
          <th>Ads as % of sales</th>
          <th>Left before the team</th>
          <th>Sales/hour to break even</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>3</td>
          <td>33.3%</td>
          <td>3.8%</td>
          <td>~$3,260</td>
        </tr>
        <tr>
          <td>4</td>
          <td>25.0%</td>
          <td>12.2%</td>
          <td>~$1,030</td>
        </tr>
        <tr>
          <td>5</td>
          <td>20.0%</td>
          <td>17.2%</td>
          <td>~$730</td>
        </tr>
        <tr>
          <td>8</td>
          <td>12.5%</td>
          <td>24.7%</td>
          <td>~$510</td>
        </tr>
      </tbody>
    </table>
  </div>

  <p><strong>Moving the target from 3 to 5 cuts the sales you need per hour by more than 4×.</strong> No staffing choice comes close. Dropping the moderator saves $112 a session. Better ads decide if the live works at all.</p>

  <p>Live looks like a production problem. It is mostly an ads problem.</p>

  <h2>Three costs the table does not show</h2>

  <p><strong>You do not get paid right away.</strong> TikTok’s <a href="https://seller-us.tiktok.com/university/essay?knowledge_id=3995852763531009&amp;lang=en" target="_blank" rel="noopener noreferrer">reserve rules</a> hold money for 30 days against returns. Your payout speed also depends on your seller tier, which TikTok resets on the first of each month.</p>

  <p><strong>Returns can lock you out of affiliates.</strong> TikTok’s <a href="https://seller-us.tiktok.com/university/essay?knowledge_id=5513514539419405&amp;lang=en" target="_blank" rel="noopener noreferrer">Shop Performance Score</a> of 3.5 unlocks affiliates and five-day payout after delivery. 4.0 unlocks one-day payout. Below 3.0, “all existing affiliate marketing plans and campaign products will be removed.”</p>

  <p>Returns lower your score. A lower score slows your cash <em>and</em> can kill the affiliate traffic that fills the live. Returns cost more than margin.</p>

  <p><strong>You cannot cut commission this week.</strong> Per TikTok’s <a href="https://seller-us.tiktok.com/university/essay?knowledge_id=6837873164896001&amp;lang=en" target="_blank" rel="noopener noreferrer">affiliate docs</a>, open-collab rate changes take <strong>30 days</strong>. Targeted collabs take 5 days. Set the rate as if you are stuck with it for a month. You are.</p>

  <h2>What we cannot tell you</h2>

  <p><strong>Nobody has published a live-only return rate.</strong> We looked in the trade press, TikTok docs, NRF, and academic papers. 19.3% is the general US online rate. Live is impulse-heavy, so yours may be higher. One study traces a path from watching live, to impulse buy, to regret, to “I might return this.” It measured intent, not real returns, and used an Indian sample. If your live return rate is 25% instead of 19.3%, break-even gets worse by about a third.</p>

  <p><strong>The ROI TikTok shows you is too high.</strong> Organic orders get credited to the campaign. This model’s ad line is therefore too kind. Compare a week with live to a week without it. Keep other spend the same.</p>

  <p><strong>Your category may look nothing like beauty.</strong> Coins, resale, and jewelry return less (or more) and sell at different hourly rates. Whatnot says coins and precious-metals sellers average over $1,000 an hour. Use this layout. Do not copy beauty’s numbers.</p>

  <p>Whatnot’s numbers also come from a company that wants more sellers and is raising money. It does not publish how it sampled. “Earnings” on that report are sales, not profit. And “stream every day and you earn more” is mostly who is already winning, not proof that more hours cause more sales.</p>

  <h2>Run this on your numbers</h2>

  <ol>
    <li><strong>Read your real TikTok fee</strong> in Seller Center. Do not trust 6% from this page if yours is different.</li>
    <li><strong>Pull last quarter’s return rate</strong> for the products you stream. This is the number most teams never check.</li>
    <li><strong>Look up your GMV Max ROI target.</strong> Ad share = 1 ÷ that target. This is usually your biggest cost.</li>
    <li><strong>Add up the team,</strong> including prep, product help, and editing. Prep often lasts longer than the live.</li>
    <li><strong>See what is left, as a percent of sales before returns.</strong> Divide your fixed session cost by that percent. That is the sales you need to break even.</li>
    <li><strong>Divide by hours on air.</strong> Compare that to every live you have already run.</li>
    <li><strong>Skip live for one week</strong> and keep other ads the same. That tells you more than a quarter of TikTok-reported ROI.</li>
  </ol>

  <p>At step 6, most teams learn two things. Break-even is higher than they thought. And they have been running some lives above it and some below it, without knowing which.</p>

  <p>For how live fits TikTok Shop overall, see <a href="/blog/live-shopping-share-tiktok-shop">live shopping is 8% of TikTok Shop</a>. For how to staff the room, see <a href="/blog/live-shopping-host">how to find a vetted live shopping host</a>. For the format itself, see <a href="/blog/what-is-live-shopping-why-this-500-billion-market-is-changing-e-commerce-forever">What Is Live Shopping</a>. For the creator side, see <a href="/blog/the-creator-s-guide-to-live-shopping-how-to-get-found-get-booked-and-get-results">The Creator's Guide to Live Shopping</a>.</p>
`;

export default liveShoppingCostContent;
